/**
 * WebRTC 1:1 link — MDN "perfect negotiation" pattern, implemented per
 * https://developer.mozilla.org/en-US/docs/Web/API/WebRTC_API/Perfect_negotiation.
 *
 * Signaling travels through the Tikron room (`rtc` intent, opaque relay — see
 * `client/net.ts` / `src/arena-room.ts`); this class knows nothing about
 * Tikron itself, only a `send` callback and an inbound `handleMessage`.
 *
 * Video/audio is an ENHANCEMENT layer: nothing here may block a game phase.
 * On a hard connection failure we try exactly one ICE restart, then surface
 * "failed" and stop — the match keeps playing without video regardless.
 */

export type ConnectionState = "connecting" | "connected" | "disconnected" | "failed" | "closed";

export interface RtcSignal {
  description?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
  /** Server-stamped sender id (see arena-room.ts's `rtc` relay). Not trusted client-side beyond logging. */
  from?: string;
}

export interface RtcLinkOptions {
  /** Wraps `room.send("rtc", payload)` — the only outbound signaling path. */
  send: (payload: RtcSignal) => void;
  /** Fired whenever a remote stream is available (may fire more than once as tracks arrive). */
  onRemoteStream: (stream: MediaStream) => void;
  /** Coarse connection status for UI (connecting/connected/failed/disconnected). */
  onConnectionState?: (state: ConnectionState) => void;
  /** A remote track muted (true) or resumed (false) — e.g. the opponent backgrounded their tab. */
  onRemoteTrackMuted?: (muted: boolean) => void;
  /** slot 2 is the polite peer (see `PlayerMatchState.slot` doc in game-core.ts). */
  polite: boolean;
  /**
   * Fetches a (possibly freshly re-minted) ICE server list. `create()` awaits
   * this ONCE, before the RTCPeerConnection even exists, so initial ICE
   * gathering starts with real servers already configured (a
   * setConfiguration patch after gathering has begun does NOT retroactively
   * regather). `reconnect()` also calls it before an ICE restart, which DOES
   * regather. Best-effort: a rejection/timeout falls back to a STUN default.
   */
  getIceServers: () => Promise<RTCIceServer[]>;
}

const VIDEO_MAX_BITRATE_BPS = 700_000;
const VIDEO_MAX_FRAMERATE = 24;
const ICE_FETCH_TIMEOUT_MS = 2000;
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [{ urls: "stun:stun.cloudflare.com:3478" }];

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error("ice-fetch-timeout")), ms);
    p.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (e: unknown) => {
        window.clearTimeout(t);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

/** Minimal shape read off `RTCStatsReport` entries — the DOM lib types these loosely. */
interface RtcStatEntry {
  type: string;
  state?: string;
  nominated?: boolean;
  localCandidateId?: string;
  remoteCandidateId?: string;
  candidateType?: string;
}

export class RtcLink {
  private readonly pc: RTCPeerConnection;
  private readonly opts: RtcLinkOptions;
  private makingOffer = false;
  private ignoreOffer = false;
  private restartedOnce = false;
  private micEnabled = true;
  private closed = false;

  private constructor(opts: RtcLinkOptions, pc: RTCPeerConnection) {
    this.opts = opts;
    this.pc = pc;
    this.wire();
  }

  /**
   * Resolve ICE servers FIRST, then construct the RTCPeerConnection with
   * them — see {@link RtcLinkOptions.getIceServers} for why this must happen
   * before the peer connection exists, not via a post-hoc setConfiguration.
   */
  static async create(opts: RtcLinkOptions): Promise<RtcLink> {
    const iceServers = await withTimeout(opts.getIceServers(), ICE_FETCH_TIMEOUT_MS).catch((err: unknown) => {
      console.warn("[rtc] fetching ICE servers failed, using STUN-only fallback:", err);
      return FALLBACK_ICE_SERVERS;
    });
    const pc = new RTCPeerConnection({ iceServers });
    return new RtcLink(opts, pc);
  }

  /** Fetch + apply a fresh ICE server list, valid ONLY around an ICE restart (which regathers). */
  private async applyFreshIceServers(): Promise<void> {
    try {
      const iceServers = await this.opts.getIceServers();
      if (this.closed) return;
      this.pc.setConfiguration({ iceServers });
    } catch (err) {
      console.warn("[rtc] fetching ICE servers failed, keeping current config:", err);
    }
  }

  private wire(): void {
    const pc = this.pc;

    pc.onnegotiationneeded = () => void this.negotiate();

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) this.opts.send({ candidate: candidate.toJSON() });
    };

    pc.ontrack = (ev) => {
      const [stream] = ev.streams;
      if (stream) this.opts.onRemoteStream(stream);
      ev.track.addEventListener("mute", () => this.opts.onRemoteTrackMuted?.(true));
      ev.track.addEventListener("unmute", () => this.opts.onRemoteTrackMuted?.(false));
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      if (state === "connected") {
        this.restartedOnce = false; // a fresh success resets the one-shot restart budget
        this.opts.onConnectionState?.("connected");
      } else if (state === "disconnected") {
        this.opts.onConnectionState?.("disconnected");
      } else if (state === "failed") {
        if (!this.restartedOnce) {
          this.restartedOnce = true;
          this.opts.onConnectionState?.("connecting");
          pc.restartIce(); // triggers onnegotiationneeded again
        } else {
          // Already tried a restart — stop trying and let the game continue
          // without video. `reconnect()` (e.g. on tab resume) can try again.
          this.opts.onConnectionState?.("failed");
        }
      } else {
        this.opts.onConnectionState?.("connecting");
      }
    };
  }

  private async negotiate(): Promise<void> {
    try {
      this.makingOffer = true;
      await this.pc.setLocalDescription();
      this.opts.send({ description: this.pc.localDescription?.toJSON() as RTCSessionDescriptionInit });
    } catch (err) {
      console.warn("[rtc] negotiation failed:", err);
    } finally {
      this.makingOffer = false;
    }
  }

  /**
   * Add local tracks from a capture stream. Safe to call with `null` (mouse
   * mode / camera denied online) — the link still negotiates and can RECEIVE
   * the opponent's media; it just sends nothing. Also safe to call more than
   * once with the same stream (e.g. a reused RtcLink across a rematch) —
   * tracks already attached to a sender are skipped, never re-added.
   */
  addLocalStream(stream: MediaStream | null): void {
    if (!stream || this.closed) return;
    const already = new Set(this.pc.getSenders().map((s) => s.track));
    for (const track of stream.getTracks()) {
      if (already.has(track)) continue;
      const sender = this.pc.addTrack(track, stream);
      if (track.kind === "video") {
        track.contentHint = "motion";
        void sender
          .setParameters({
            ...sender.getParameters(),
            encodings: [{ maxBitrate: VIDEO_MAX_BITRATE_BPS, maxFramerate: VIDEO_MAX_FRAMERATE }],
          })
          .catch((err: unknown) => console.warn("[rtc] setParameters failed:", err));
      } else if (track.kind === "audio") {
        track.enabled = this.micEnabled;
      }
    }
  }

  setMicEnabled(enabled: boolean): void {
    this.micEnabled = enabled;
    for (const sender of this.pc.getSenders()) {
      if (sender.track?.kind === "audio") sender.track.enabled = enabled;
    }
  }

  /** True once a local audio track has actually been added (post addLocalStream). */
  hasAudioSender(): boolean {
    return this.pc.getSenders().some((s) => s.track?.kind === "audio");
  }

  /** Handle one relayed signaling message. `from` is the server-stamped sender (informational). */
  async handleMessage(payload: RtcSignal, from: string): Promise<void> {
    void from; // 1:1 room — there is only ever one possible sender; kept for the documented signature
    if (this.closed) return;
    try {
      if (payload.description) {
        const description = payload.description;
        const offerCollision =
          description.type === "offer" && (this.makingOffer || this.pc.signalingState !== "stable");
        this.ignoreOffer = !this.opts.polite && offerCollision;
        if (this.ignoreOffer) return;

        await this.pc.setRemoteDescription(description);
        if (description.type === "offer") {
          await this.pc.setLocalDescription();
          this.opts.send({ description: this.pc.localDescription?.toJSON() as RTCSessionDescriptionInit });
        }
      } else if (payload.candidate) {
        try {
          await this.pc.addIceCandidate(payload.candidate);
        } catch (err) {
          if (!this.ignoreOffer) throw err;
        }
      }
    } catch (err) {
      console.warn("[rtc] handleMessage failed:", err);
    }
  }

  /** Manual reconnect attempt (e.g. tab resume finds us failed/disconnected). */
  reconnect(): void {
    if (this.closed) return;
    this.restartedOnce = false; // grant a fresh one-shot restart budget
    // Best-effort: refresh ICE servers (TURN creds are ~1h lived) before
    // restarting — a rejected fetch just leaves the current config in place.
    // Valid here (unlike at construction) because restartIce() regathers.
    void this.applyFreshIceServers().finally(() => {
      if (!this.closed) this.pc.restartIce();
    });
  }

  /**
   * Close the peer connection only. Local tracks are NOT stopped here — they
   * are the SAME MediaStreamTrack objects as `camStream`'s (this class never
   * creates its own), so stopping them would kill the game's face-detection
   * pipeline on every teardown-then-continue path (opponent-left, lobby
   * reset, recreate-after-failure). Track lifecycle belongs solely to
   * `camera.ts` / `stopCamera`.
   */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.pc.close();
  }

  get connectionState(): RTCPeerConnectionState {
    return this.pc.connectionState;
  }

  /**
   * The currently selected ICE candidate pair's types (e.g. "host", "srflx",
   * "relay"), for diagnostics. Null if there is no succeeded pair yet (or
   * `getStats()` failed).
   */
  async getSelectedCandidatePair(): Promise<{ local: string; remote: string } | null> {
    try {
      const stats = await this.pc.getStats();
      let pair: RtcStatEntry | undefined;
      stats.forEach((v: unknown) => {
        const report = v as RtcStatEntry;
        if (report.type === "candidate-pair" && report.state === "succeeded" && report.nominated !== false) {
          pair = report;
        }
      });
      if (!pair) return null;
      const local = stats.get(pair.localCandidateId ?? "") as RtcStatEntry | undefined;
      const remote = stats.get(pair.remoteCandidateId ?? "") as RtcStatEntry | undefined;
      if (!local || !remote) return null;
      return { local: local.candidateType ?? "unknown", remote: remote.candidateType ?? "unknown" };
    } catch (err) {
      console.warn("[rtc] getStats (selected pair) failed:", err);
      return null;
    }
  }

  /** Counts of gathered LOCAL candidates by type (e.g. `{host: 2, srflx: 1}`) — diagnostics for a "failed" state. */
  async getLocalCandidateTypeCounts(): Promise<Record<string, number>> {
    const counts: Record<string, number> = {};
    try {
      const stats = await this.pc.getStats();
      stats.forEach((v: unknown) => {
        const report = v as RtcStatEntry;
        if (report.type === "local-candidate" && report.candidateType) {
          counts[report.candidateType] = (counts[report.candidateType] ?? 0) + 1;
        }
      });
    } catch (err) {
      console.warn("[rtc] getStats (local candidate counts) failed:", err);
    }
    return counts;
  }
}
